/**
 * @file index.ts
 * @description Initializes the i18n internationalization framework for the agent dashboard application, setting up language resources for English, Chinese, and Vietnamese locales. It configures language detection, fallback options, and namespaces for organized translation keys. This module allows the application to support multiple languages and provides a seamless experience for users across different regions.
 * @author Son Nguyen <hoangson091104@gmail.com>
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import common_en from "./locales/en/common.json";
import common_zh from "./locales/zh/common.json";
import common_vi from "./locales/vi/common.json";
import nav_en from "./locales/en/nav.json";
import nav_zh from "./locales/zh/nav.json";
import nav_vi from "./locales/vi/nav.json";
import dashboard_en from "./locales/en/dashboard.json";
import dashboard_zh from "./locales/zh/dashboard.json";
import dashboard_vi from "./locales/vi/dashboard.json";
import sessions_en from "./locales/en/sessions.json";
import sessions_zh from "./locales/zh/sessions.json";
import sessions_vi from "./locales/vi/sessions.json";
import activity_en from "./locales/en/activity.json";
import activity_zh from "./locales/zh/activity.json";
import activity_vi from "./locales/vi/activity.json";
import analytics_en from "./locales/en/analytics.json";
import analytics_zh from "./locales/zh/analytics.json";
import analytics_vi from "./locales/vi/analytics.json";
import workflows_en from "./locales/en/workflows.json";
import workflows_zh from "./locales/zh/workflows.json";
import workflows_vi from "./locales/vi/workflows.json";
import settings_en from "./locales/en/settings.json";
import settings_zh from "./locales/zh/settings.json";
import settings_vi from "./locales/vi/settings.json";
import kanban_en from "./locales/en/kanban.json";
import kanban_zh from "./locales/zh/kanban.json";
import kanban_vi from "./locales/vi/kanban.json";
import errors_en from "./locales/en/errors.json";
import errors_zh from "./locales/zh/errors.json";
import errors_vi from "./locales/vi/errors.json";
import updates_en from "./locales/en/updates.json";
import updates_zh from "./locales/zh/updates.json";
import updates_vi from "./locales/vi/updates.json";
import ccConfig_en from "./locales/en/ccConfig.json";
import ccConfig_zh from "./locales/zh/ccConfig.json";
import ccConfig_vi from "./locales/vi/ccConfig.json";
import run_en from "./locales/en/run.json";
import run_zh from "./locales/zh/run.json";
import run_vi from "./locales/vi/run.json";
import infrastructure_en from "./locales/en/infrastructure.json";
import infrastructure_zh from "./locales/zh/infrastructure.json";
import infrastructure_vi from "./locales/vi/infrastructure.json";
// Note: commandCenter strings are intentionally Polish in all three locales —
// this is a personal instance and Polish is the deliberate language of that page.
import commandCenter_en from "./locales/en/commandCenter.json";
import commandCenter_zh from "./locales/zh/commandCenter.json";
import commandCenter_vi from "./locales/vi/commandCenter.json";
// Note: inbox strings are intentionally Polish in all three locales as well —
// same convention as commandCenter (personal instance, deliberate Polish page).
import inbox_en from "./locales/en/inbox.json";
import inbox_zh from "./locales/zh/inbox.json";
import inbox_vi from "./locales/vi/inbox.json";
// Note: the life-layer pages (today/tasks/routines) follow the same
// intentionally-Polish convention as inbox/commandCenter.
import today_en from "./locales/en/today.json";
import today_zh from "./locales/zh/today.json";
import today_vi from "./locales/vi/today.json";
import tasks_en from "./locales/en/tasks.json";
import tasks_zh from "./locales/zh/tasks.json";
import tasks_vi from "./locales/vi/tasks.json";
import routines_en from "./locales/en/routines.json";
import routines_zh from "./locales/zh/routines.json";
import routines_vi from "./locales/vi/routines.json";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: common_en,
        nav: nav_en,
        dashboard: dashboard_en,
        sessions: sessions_en,
        activity: activity_en,
        analytics: analytics_en,
        workflows: workflows_en,
        settings: settings_en,
        kanban: kanban_en,
        errors: errors_en,
        updates: updates_en,
        ccConfig: ccConfig_en,
        run: run_en,
        infrastructure: infrastructure_en,
        commandCenter: commandCenter_en,
        inbox: inbox_en,
        today: today_en,
        tasks: tasks_en,
        routines: routines_en,
      },
      zh: {
        common: common_zh,
        nav: nav_zh,
        dashboard: dashboard_zh,
        sessions: sessions_zh,
        activity: activity_zh,
        analytics: analytics_zh,
        workflows: workflows_zh,
        settings: settings_zh,
        kanban: kanban_zh,
        errors: errors_zh,
        updates: updates_zh,
        ccConfig: ccConfig_zh,
        run: run_zh,
        infrastructure: infrastructure_zh,
        commandCenter: commandCenter_zh,
        inbox: inbox_zh,
        today: today_zh,
        tasks: tasks_zh,
        routines: routines_zh,
      },
      vi: {
        common: common_vi,
        nav: nav_vi,
        dashboard: dashboard_vi,
        sessions: sessions_vi,
        activity: activity_vi,
        analytics: analytics_vi,
        workflows: workflows_vi,
        settings: settings_vi,
        kanban: kanban_vi,
        errors: errors_vi,
        updates: updates_vi,
        ccConfig: ccConfig_vi,
        run: run_vi,
        infrastructure: infrastructure_vi,
        commandCenter: commandCenter_vi,
        inbox: inbox_vi,
        today: today_vi,
        tasks: tasks_vi,
        routines: routines_vi,
      },
    },
    supportedLngs: ["en", "zh", "vi"],
    nonExplicitSupportedLngs: true,
    fallbackLng: "en",
    ns: [
      "common",
      "nav",
      "dashboard",
      "sessions",
      "activity",
      "analytics",
      "workflows",
      "settings",
      "kanban",
      "errors",
      "updates",
      "ccConfig",
      "run",
      "infrastructure",
      "commandCenter",
      "inbox",
      "today",
      "tasks",
      "routines",
    ],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
  });

export default i18n;
